import { useCallback, useRef, useState } from 'react';

import { useStorageToken } from '~lib/auth/use-storage-token';
import { uploadBlockBlob } from '~lib/storage/upload-blob';

export type FileStatus = 'queued' | 'uploading' | 'done' | 'failed' | 'cancelled';

export interface FileUpload {
  file: File;
  status: FileStatus;
  bytesUploaded: number;
  error?: string;
}

export type UploadState = 'idle' | 'running' | 'done' | 'cancelled' | 'failed';

interface UseUploadDatasetResult {
  state: UploadState;
  uploads: FileUpload[];
  totalBytes: number;
  bytesUploaded: number;
  start: (datasetName: string, files: File[]) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

export function useUploadDataset(): UseUploadDatasetResult {
  const getToken = useStorageToken();
  const [state, setState] = useState<UploadState>('idle');
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const totalBytes = uploads.reduce((sum, u) => sum + u.file.size, 0);
  const bytesUploaded = uploads.reduce((sum, u) => sum + u.bytesUploaded, 0);

  const updateUpload = useCallback(
    (index: number, patch: Partial<FileUpload>) => {
      setUploads((prev) => {
        const next = prev.slice();
        next[index] = { ...next[index], ...patch };
        return next;
      });
    },
    [],
  );

  const start = useCallback(
    async (datasetName: string, files: File[]) => {
      const initial: FileUpload[] = files.map((file) => ({
        file,
        status: 'queued',
        bytesUploaded: 0,
      }));
      setUploads(initial);
      setState('running');

      const controller = new AbortController();
      abortRef.current = controller;

      let anyFailed = false;
      try {
        const token = await getToken();

        for (let i = 0; i < files.length; i++) {
          if (controller.signal.aborted) break;
          const file = files[i];
          updateUpload(i, { status: 'uploading' });
          // Preserve any folder structure from `webkitRelativePath` when the
          // user picked a directory; otherwise use the bare filename. Strip
          // a leading path segment if it duplicates the dataset name (folder
          // pickers include the chosen directory name in the relative path).
          const relativePath = (file as File & { webkitRelativePath?: string })
            .webkitRelativePath;
          const stripped = relativePath
            ? relativePath.replace(/^[^/]+\//, '')
            : file.name;
          const fullPath = `${datasetName}/${stripped}`;
          try {
            await uploadBlockBlob(token, fullPath, file, {
              signal: controller.signal,
              onProgress: (bytes) => updateUpload(i, { bytesUploaded: bytes }),
            });
            updateUpload(i, { status: 'done', bytesUploaded: file.size });
          } catch (e) {
            if (
              e instanceof DOMException && e.name === 'AbortError'
            ) {
              updateUpload(i, { status: 'cancelled' });
            } else {
              anyFailed = true;
              updateUpload(i, {
                status: 'failed',
                error: e instanceof Error ? e.message : String(e),
              });
            }
          }
        }

        if (controller.signal.aborted) {
          setState('cancelled');
        } else if (anyFailed) {
          setState('failed');
        } else {
          setState('done');
        }
      } catch (e) {
        // Token acquisition failure or other startup error.
        setUploads((prev) =>
          prev.map((u) => ({
            ...u,
            status: 'failed',
            error: e instanceof Error ? e.message : String(e),
          })),
        );
        setState('failed');
      } finally {
        abortRef.current = null;
      }
    },
    [getToken, updateUpload],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    setUploads([]);
    setState('idle');
  }, []);

  return { state, uploads, totalBytes, bytesUploaded, start, cancel, reset };
}
